import { RowConfig } from "../types";
import CryptoJS from "crypto-js";

async function sha256(data: string): Promise<string> {
  // Try to use native Web Crypto API first (faster)
  if (typeof crypto !== "undefined" && crypto.subtle && crypto.subtle.digest) {
    try {
      const hash = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(data),
      );
      return Array.from(new Uint8Array(hash))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    } catch (error) {
      console.warn("Web Crypto API failed, falling back to crypto-js:", error);
    }
  }

  // Fallback to crypto-js for environments where Web Crypto API is not available
  // (like MetaMask mobile browser)
  const hash = CryptoJS.SHA256(data);
  return hash.toString(CryptoJS.enc.Hex);
}

export interface DeathTile {
  row: number;
  position: number;
  totalTiles: number;
}

export interface GameVerificationResult {
  valid: boolean;
  commitmentHash: string;
  error?: string;
  deathTiles?: DeathTile[];
}

export async function getDeathTileIndex(
  seed: string,
  rowIndex: number,
  totalTiles: number,
): Promise<number> {
  // Create unique string for this row
  const hashSource = `${seed}-row${rowIndex}`;

  // Generate SHA-256 hash
  const hash = await sha256(hashSource);

  // Convert first 8 hex characters to number
  const numericHash = parseInt(hash.slice(0, 8), 16);

  // Use modulo to get position 0 to totalTiles-1
  return numericHash % totalTiles;
}

export async function generateAllDeathTiles(
  seed: string,
  rowConfigs: RowConfig[],
): Promise<DeathTile[]> {
  const deathTiles: DeathTile[] = [];

  for (let rowIndex = 0; rowIndex < rowConfigs.length; rowIndex++) {
    const totalTiles = rowConfigs[rowIndex].tiles;
    const deathTileIndex = await getDeathTileIndex(seed, rowIndex, totalTiles);

    deathTiles.push({
      row: rowIndex,
      position: deathTileIndex,
      totalTiles: totalTiles,
    });
  }

  return deathTiles;
}

export async function createCommitmentHash(
  version: string,
  rows: RowConfig[],
  seed: string,
): Promise<string> {
  const gameData = JSON.stringify({
    version, // Algorithm version (e.g., "v1")
    rows, // Array of row configurations
    seed, // Random game seed
  });

  return "0x" + (await sha256(gameData));
}

export async function verifyGame(
  seed: string,
  rowConfigs: RowConfig[],
  commitmentHash: string,
  version: string = "v1",
): Promise<GameVerificationResult> {
  // Recreate the commitment hash
  const expectedHash = await createCommitmentHash(version, rowConfigs, seed);

  // Verify the hash matches
  if (expectedHash !== commitmentHash) {
    return {
      valid: false,
      error: "Commitment hash does not match",
      commitmentHash: expectedHash,
    };
  }

  // Generate the death tiles
  const deathTiles = await generateAllDeathTiles(seed, rowConfigs);

  return {
    valid: true,
    deathTiles,
    commitmentHash: expectedHash,
  };
}
